/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/system";

// prop-types is a library for typechecking of props

import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// sweetalert2 components
import Swal from "sweetalert2";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import CreateTemplate from "Modals/CreateTemplate";
import CheckObjectDeletion from "Modals/CheckObjectDeletion";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

// Data
//import dataTableData from "views/templates-list/data/dataTableData";

// ProductsList page components
import ActionCell from "views/templates-list/components/ActionCell";

import { getData, postData, delData, putData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import { useSoftUIController } from "context";
import { Alert, Button, Grid, Icon, Snackbar } from "@mui/material";
import SoftSelect from "components/SoftSelect";

// Badges
const disabledTemplate = (
  <SoftBadge variant="contained" color="error" size="xs" badgeContent="Disabled" container />
);
const enabledTemplate = (
  <SoftBadge variant="contained" color="success" size="xs" badgeContent="Enabled" container />
);

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

let currentData = [];
let templateOddsFilters = [];
let oddSettingsList = [];
let oddSettingsOptions = [];
let marketSettingsOptions = [];
let oddSettingsEditCached = false;
let oddSettingsShowCached = false;

/**
 *
 * @returns
 */
function TemplatesList() {
  const abortController = new AbortController();

  const [controller, dispatch] = useSoftUIController();
  const { platform, permissions } = controller;
  const userData = getUserData();

  const [selectedRows, setSelectedRows] = useState([]);
  const [showCreateModal, setCreateModal] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [updateRecords, setUpdateRecords] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(0);
  const [recordNameSelected, setRecordNameSelected] = useState(null);
  const [newName, setNewName] = useState("");
  const [templateCreatedId, setTemplateCreatedId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hiddenColumns, setHiddenColumns] = useState([
    "selected",
    "odds_filter_inplay_id",
    "odds_filter_pregame_id",
  ]);
  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "selected", accessor: "selected", width: "100px" },
      {
        Header: "template",
        accessor: "template",
        Cell: makeLinkCell,
        width: "40%",
      },
      {
        Header: "filters",
        width: "40%",
        Cell: makeFiltersCell,
        accessor: "filters",
      },
      {
        Header: "disabled Markets",
        width: "40%",
        Cell: makeFiltersCell,
        accessor: "dis_markets",
      },
      {
        Header: "Markets Setting",
        width: "40%",
        Cell: formatMarketsSetting,
        accessor: "markets_filter_config_id",
      },
      {
        Header: "status",
        accessor: "status",
        width: "120px",
        Cell: makeStatusCell,
      },
      {
        Header: "Odds Settings inplay",
        accessor: "odds_filter_inplay_id",
        width: "120px",
        Cell: formatOddSettingInplay,
      },
      {
        Header: "Odds Settings pregame",
        accessor: "odds_filter_pregame_id",
        width: "120px",
        Cell: formatOddSettingPregame,
      },
      { Header: "action", width: "120px", accessor: "action" },
    ],

    rows: [],
  });

  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [oddSettingsAvailable, setSettingsAvailable] = useState(null);
  const [marketSettingsAvailable, setMarketSettingsAvailable] = useState(null);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [userPermissions, setUserPermissions] = useState(null);
  const [oddSettingsEdit, setOddSettingsEdit] = useState(false);
  const [oddSettingsShow, setOddSettingsShow] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const mixingOptions = [
    { value: 0, label: "No Mixing" },
    { value: 1, label: "Highest Price" },
    { value: 2, label: "Percentage Weight" },
  ];

  const oddsReactions = [
    { value: 0, label: "Do Nothing" },
    { value: 1, label: "Suspend Odds" },
    { value: 2, label: "Set Odds to Limits" },
    { value: 3, label: "Suspend Market" },
  ];

  const marginOptions = [
    { value: 0, label: "No Margin" },
    { value: 1, label: "All odds get same margin (EM)" },
    { value: 2, label: "Highest odds get more margin (MPTO)" },
    { value: 3, label: "Lowest odds get more margin (MPTP)" },
  ];

  const oddsSuspensionType = [
    { value: 0, label: "Never" },
    { value: 1, label: "One Provider Suspend Odd" },
    { value: 2, label: "All Providers Suspend Odd" },
    { value: 3, label: "The Most off the Providers Suspend Odd" },
    { value: 4, label: "Ordered Providers Suspend Odd" },
    { value: 5, label: "Price is higher than the sorted Providers" },
  ];

  let timerInterval;

  const platforms = userData
    ? userData.platforms
      ? userData.platforms.map((platform) => ({
          value: platform.id,
          label: platform.name,
          logo: platform.logo,
          sorting: platform.sorting,
          odds_styles: platform.odds_styles,
        }))
      : []
    : [];

  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeFiltersCell({ value, ...rest }) {
    return value === "" ? "None Filter Added" : value;
  }

  function getOddSettingsOptions() {
    return oddSettingsOptions;
  }

  function getMarketSettingsOptions() {
    return marketSettingsOptions;
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeLinkCell({ value, ...rest }) {
    if (rest.row.cells[1].value == "") {
      return (
        <>
          <Link to={`/feed-settings/filters/create-filter`}>
            <SoftTypography
              component="span"
              variant="button"
              fontWeight="regular"
              color={"dark"}
              opacity={0.8}
              sx={{ lineHeight: 0 }}
            >
              {value}
            </SoftTypography>
          </Link>
        </>
      );
    } else {
      let templateId = rest.data.find((item) => item.template == value).id;
      return (
        <>
          {/* <Link to={`/feed-settings/filters/filters-list/${templateId}`}> */}
          <Link to={`/feed-settings/filters`} state={{ templateId: templateId }}>
            <SoftTypography
              component="span"
              variant="button"
              fontWeight="regular"
              color={"dark"}
              opacity={0.8}
              sx={{ lineHeight: 0 }}
            >
              {value}
            </SoftTypography>
          </Link>
        </>
      );
    }
  }
  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeStatusCell({ value, ...rest }) {
    return value === "Enabled" ? enabledTemplate : disabledTemplate;
  }

  function formatOddSettingInplay({ value, ...rest }) {
    return value ? (
      <SoftBox className="odd-setting-menu-holder">
        {/* <SoftSelect
          customSmall={true}
          options={oddSettingsAvailable}
          value={oddSettingsAvailable.find((item) => item.value === value)}
        ></SoftSelect> */}
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0, fontSize: "1rem", verticalAlign: "sub" }}
        >
          <Tooltip title="Change Odd Settings" placement="top">
            <Icon
              style={{
                display: rest.row.original.odds_setting_inplay ? "" : "none",
                color: rest.row.original.odds_setting_inplay
                  ? rest.row.original.operOddSettings_inplay
                    ? "#ff000070"
                    : "rgb(203, 12, 159)"
                  : "",
              }}
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "add",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              {rest.row.original.odds_setting_inplay
                ? rest.row.original.operOddSettings_inplay
                  ? "playlist_remove_icon"
                  : "playlist_add_circle_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.odds_setting_inplay && !rest.row.original.operOddSettings_inplay
                ? ""
                : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "remove",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operOddSettings_inplay ? "odd-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={getOddSettingsOptions()}
          value={
            getOddSettingsOptions()
              ? getOddSettingsOptions().find(
                  (item) => item.value === rest.row.original.odds_setting_inplay
                )
              : null
          }
          onChange={(item) => rowOddSettingsChange(item, rest.row.original, "inplay")}
        ></SoftSelect>
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{}}>
                {rest.row.original.odd_settings_info_inplay.map((item, index) => {
                  return (
                    <div key={index} style={{}}>
                      <div style={{ fontWeight: "bold" }}>{item.title}</div>
                      <ul style={{ marginLeft: "5px", paddingLeft: "10px" }}>
                        {item.settings.map((s, i) => {
                          let print = s.value ? (s.key ? s.key + ": " + s.value : s.value) : "";
                          return print == "" ? false : <li key={i}>{print}</li>;
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          }
        >
          <Button
            sx={{
              maxWidth: "15px",
              width: "15px",
              minWidth: "10px",
              marginRight: "5px",
              display: rest.row.original.odds_setting_inplay ? "none" : "",
            }}
          >
            <Icon>info_outlined</Icon>
          </Button>
        </HtmlTooltip>

        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            marginRight: "5px",
            display: oddSettingsEditCached
              ? rest.row.original.odds_setting_inplay
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Edit Filter's Odd Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "edit",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              edit
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached
              ? rest.row.original.odds_setting_inplay
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Delete Filter's Odd Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "delete",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              delete_outlined
            </Icon>
          </Tooltip>
        </SoftTypography>
      </SoftBox>
    ) : (
      <SoftBox className="odd-setting-menu-holder">
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached ? "" : "none",
          }}
        >
          <Tooltip
            title={
              rest.row.original.odds_setting_inplay
                ? "Change Odd Settings"
                : "Add Filter's Odd Settings"
            }
            placement="top"
          >
            <Icon
              style={{
                color: rest.row.original.odds_setting_inplay
                  ? "rgb(203, 12, 159)"
                  : rest.row.original.operOddSettings_inplay
                  ? "#ff000070"
                  : "",
              }}
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "add",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              {rest.row.original.odds_setting_inplay
                ? "playlist_add_circle_icon"
                : rest.row.original.operOddSettings_inplay
                ? "playlist_remove_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display:
              oddSettingsShowCached &&
              !oddSettingsEditCached &&
              !rest.row.original.odds_setting_inplay
                ? ""
                : "none",
          }}
        >
          None
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.odds_setting_inplay && !rest.row.original.operOddSettings_inplay
                ? ""
                : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "remove",
                  data: rest.row.original.id,
                  type: "inplay",
                })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operOddSettings_inplay ? "odd-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={getOddSettingsOptions()}
          value={
            getOddSettingsOptions()
              ? getOddSettingsOptions().find(
                  (item) =>
                    item.value === value || item.value === rest.row.original.odds_setting_inplay
                )
              : null
          }
          onChange={(item) => rowOddSettingsChange(item, rest.row.original, "inplay")}
        ></SoftSelect>
      </SoftBox>
    );
  }

  function formatOddSettingPregame({ value, ...rest }) {
    return value ? (
      <SoftBox className="odd-setting-menu-holder">
        {/* <SoftSelect
          customSmall={true}
          options={oddSettingsAvailable}
          value={oddSettingsAvailable.find((item) => item.value === value)}
        ></SoftSelect> */}
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0, fontSize: "1rem", verticalAlign: "sub" }}
        >
          <Tooltip title="Change Odd Settings" placement="top">
            <Icon
              style={{
                display: rest.row.original.odds_setting_pregame ? "" : "none",
                color: rest.row.original.odds_setting_pregame
                  ? rest.row.original.operOddSettings_pregame
                    ? "#ff000070"
                    : "rgb(203, 12, 159)"
                  : "",
              }}
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "add",
                  data: rest.row.original.id,
                  type: "pregame",
                })
              }
            >
              {rest.row.original.odds_setting_pregame
                ? rest.row.original.operOddSettings_pregame
                  ? "playlist_remove_icon"
                  : "playlist_add_circle_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.odds_setting_pregame && !rest.row.original.operOddSettings_pregame
                ? ""
                : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "remove",
                  data: rest.row.original.id,
                  type: "pregame",
                })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operOddSettings_pregame ? "odd-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={getOddSettingsOptions()}
          value={
            getOddSettingsOptions()
              ? getOddSettingsOptions().find(
                  (item) => item.value === rest.row.original.odds_setting_pregame
                )
              : null
          }
          onChange={(item) => rowOddSettingsChange(item, rest.row.original, "pregame")}
        ></SoftSelect>
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{}}>
                {rest.row.original.odd_settings_info_pregame.map((item, index) => {
                  return (
                    <div key={index} style={{}}>
                      <div style={{ fontWeight: "bold" }}>{item.title}</div>
                      <ul style={{ marginLeft: "5px", paddingLeft: "10px" }}>
                        {item.settings.map((s, i) => {
                          let print = s.value ? (s.key ? s.key + ": " + s.value : s.value) : "";
                          return print == "" ? false : <li key={i}>{print}</li>;
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          }
        >
          <Button
            sx={{
              maxWidth: "15px",
              width: "15px",
              minWidth: "10px",
              marginRight: "5px",
              display: rest.row.original.odds_setting_pregame ? "none" : "",
            }}
          >
            <Icon>info_outlined</Icon>
          </Button>
        </HtmlTooltip>

        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            marginRight: "5px",
            display: oddSettingsEditCached
              ? rest.row.original.odds_setting_pregame
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Edit Filter's Odd Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "edit",
                  data: rest.row.original.id,
                  type: "pregame",
                })
              }
            >
              edit
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached
              ? rest.row.original.odds_setting_pregame
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Delete Filter's Odd Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "delete",
                  data: rest.row.original.id,
                  type: "pregame",
                })
              }
            >
              delete_outlined
            </Icon>
          </Tooltip>
        </SoftTypography>
      </SoftBox>
    ) : (
      <SoftBox className="odd-setting-menu-holder">
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached ? "" : "none",
          }}
        >
          <Tooltip
            title={
              rest.row.original.odds_setting_pregame
                ? "Change Odd Settings"
                : "Add Filter's Odd Settings"
            }
            placement="top"
          >
            <Icon
              style={{
                color: rest.row.original.odds_setting_pregame
                  ? "rgb(203, 12, 159)"
                  : rest.row.original.operOddSettings_pregame
                  ? "#ff000070"
                  : "",
              }}
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "add",
                  data: rest.row.original.id,
                  type: "pregame",
                })
              }
            >
              {rest.row.original.odds_setting_pregame
                ? "playlist_add_circle_icon"
                : rest.row.original.operOddSettings_pregame
                ? "playlist_remove_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display:
              oddSettingsShowCached &&
              !oddSettingsEditCached &&
              !rest.row.original.operOddSettings_pregame
                ? ""
                : "none",
          }}
        >
          None
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.odds_setting_pregame && !rest.row.original.operOddSettings_pregame
                ? ""
                : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, {
                  action: "remove",
                  data: rest.row.original.id,
                  type: "pregame",
                })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operOddSettings_pregame ? "odd-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={getOddSettingsOptions()}
          value={
            getOddSettingsOptions()
              ? getOddSettingsOptions().find(
                  (item) =>
                    item.value === value || item.value === rest.row.original.odds_setting_pregame
                )
              : null
          }
          onChange={(item) => rowOddSettingsChange(item, rest.row.original, "pregame")}
        ></SoftSelect>
      </SoftBox>
    );
  }

  function formatMarketsSetting({ value, ...rest }) {
    return value ? (
      <SoftBox className="market-setting-menu-holder">
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0, fontSize: "1rem", verticalAlign: "sub" }}
        >
          <Tooltip title="Change Markets Settings" placement="top">
            <Icon
              style={{
                display: rest.row.original.markets_setting_id ? "" : "none",
                color: rest.row.original.markets_setting_id
                  ? rest.row.original.operMarketSettings_id
                    ? "#ff000070"
                    : "rgb(203, 12, 159)"
                  : "",
              }}
              onClick={(e) =>
                handleMarketSettingSelection(e, {
                  action: "add",
                  data: rest.row.original.id,
                })
              }
            >
              {rest.row.original.markets_setting_id
                ? rest.row.original.operMarketSettings_id
                  ? "playlist_remove_icon"
                  : "playlist_add_circle_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.markets_setting_id && !rest.row.original.operMarketSettings_id
                ? ""
                : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleMarketSettingSelection(e, {
                  action: "remove",
                  data: rest.row.original.id,
                })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operMarketSettings_id ? "market-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={getMarketSettingsOptions()}
          value={
            getMarketSettingsOptions()
              ? getMarketSettingsOptions().find(
                  (item) => item.value === rest.row.original.markets_setting_id
                )
              : null
          }
          onChange={(item) => rowMarketSettingsChange(item, rest.row.original)}
        ></SoftSelect>

        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            marginRight: "5px",
            display: oddSettingsEditCached
              ? rest.row.original.markets_setting_id
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Edit Filter's Market Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleMarketSettingSelection(e, {
                  action: "edit",
                  data: rest.row.original.id,
                })
              }
            >
              edit
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached
              ? rest.row.original.markets_setting_id
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Delete Filter's Market Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleMarketSettingSelection(e, {
                  action: "delete",
                  data: rest.row.original.id,
                })
              }
            >
              delete_outlined
            </Icon>
          </Tooltip>
        </SoftTypography>
      </SoftBox>
    ) : (
      <SoftBox className="market-setting-menu-holder">
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached ? "" : "none",
          }}
        >
          <Tooltip
            title={
              rest.row.original.markets_setting_id
                ? "Change Market Settings"
                : "Add Filter's Market Settings"
            }
            placement="top"
          >
            <Icon
              style={{
                color: rest.row.original.markets_setting_id
                  ? "rgb(203, 12, 159)"
                  : rest.row.original.operMarketSettings_id
                  ? "#ff000070"
                  : "",
              }}
              onClick={(e) =>
                handleMarketSettingSelection(e, {
                  action: "add",
                  data: rest.row.original.id,
                  type: "pregame",
                })
              }
            >
              {rest.row.original.markets_setting_id
                ? "playlist_add_circle_icon"
                : rest.row.original.operMarketSettings_id
                ? "playlist_remove_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display:
              oddSettingsShowCached &&
              !oddSettingsEditCached &&
              !rest.row.original.operMarketSettings_id
                ? ""
                : "none",
          }}
        >
          None
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.markets_setting_id && !rest.row.original.operMarketSettings_id
                ? ""
                : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleMarketSettingSelection(e, {
                  action: "remove",
                  data: rest.row.original.id,
                })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operMarketSettings_id ? "market-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={getMarketSettingsOptions()}
          value={
            getMarketSettingsOptions()
              ? getMarketSettingsOptions().find(
                  (item) =>
                    item.value === value || item.value === rest.row.original.markets_setting_id
                )
              : null
          }
          onChange={(item) => rowMarketSettingsChange(item, rest.row.original)}
        ></SoftSelect>
      </SoftBox>
    );
  }

  /**
   *
   */
  const handleCreateRecord = () => {
    setCreateModal(true);
  };

  /**
   *
   */
  const handleEditRecord = (e, id, name) => {
    setRecordToDelete(id);
    setRecordNameSelected(name);
    setCreateModal(true);
  };

  /**
   *
   * @param {*} e
   * @param {*} id
   */
  const handleOpenModal = (e, id) => {
    setRecordToDelete(id);
    setDeleteModal(true);
  };

  /**
   *
   * @param {*} e
   * @param {*} id
   */
  const handleApplyAction = async (e, template) => {
    //console.log("id", template.id);
    let templateData = template;
    let templateToSend = {
      name: templateData.name,
      status: templateData.status === "Enabled" ? false : true,
      platform_id: templateData.platform_id,
    };

    function returnId() {
      return templateData.id;
    }

    function returnTemplateToSend() {
      return templateToSend;
    }

    Swal.fire({
      title: "We proccess your request! ",
      html: "Please Wait a few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        let setTemplatesEndpoint = `/api/v1/templates/${returnId()}`;

        let response = await putData(setTemplatesEndpoint, returnTemplateToSend(), {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      initData();
      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  };

  /**
   *
   * @param {*} e
   * @param {*} id
   */
  const handleCloneAction = async (e, template) => {
    //console.log("id", template.id);
    let templateData = template;
    let templateToSend = {
      name: templateData.name,
      status: templateData.status === "Enabled" ? false : true,
      platform_id: templateData.platform_id,
    };

    function returnId() {
      return templateData.id;
    }

    function returnTemplateToSend() {
      return templateToSend;
    }

    Swal.fire({
      title: "We proccess your request! ",
      html: "Please Wait a few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        let setTemplatesEndpoint = `/api/v1/templates/${returnId()}/clone`;

        let response = await postData(setTemplatesEndpoint, returnTemplateToSend(), {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      initData();
      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  };

  /**
   *
   * @param {*} e
   * @param {*} id
   */
  const handleCheckCloneAction = async (e, template) => {
    //console.log("id", template.id);
    let templateData = template;
    let templateToSend = {
      name: templateData.name,
      status: templateData.status === "Enabled" ? false : true,
      platform_id: templateData.platform_id,
    };

    function returnId() {
      return templateData.id;
    }

    function returnTemplateToSend() {
      return templateToSend;
    }

    let selectedPlatforms = {};

    Swal.fire({
      title: "We proccess your request! ",
      html: "Please Wait a few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        let checkCloneEndpoint = `/api/v1/templates/${returnId()}/checkclone`;

        let response = await postData(checkCloneEndpoint, returnTemplateToSend(), {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            if (response.data.length == 0) {
              Swal.clickConfirm();
            } else {
              for (let index = 0; index < response.data.length; index++) {
                const platform = response.data[index];
                let platformExist = platforms.find((p) => p.value == platform * 1);
                if (platformExist) {
                  selectedPlatforms[platformExist.value] = platformExist.label;
                }
              }
              Swal.clickConfirm();
            }
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then(async (result) => {
      /* Read more about handling dismissals below */
      if (result.isConfirmed) {
        if (Object.keys(selectedPlatforms).length > 0) {
          const { value: platform } = await Swal.fire({
            icon: "question",
            title: "Select the Platform you want to copy the selected Template",
            input: "select",
            inputOptions: selectedPlatforms,
            inputPlaceholder: "Select Platform",
            showCancelButton: true,
            confirmButtonColor: "#87cb16",
            cancelButtonColor: "#ff4a55",
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (value) {
                  resolve();
                } else {
                  resolve("You need to select a platform or press cancel.");
                }
              });
            },
          });
          if (platform) {
            template.platform_id = platform * 1;
            handleCloneAction(null, template);
          }
        } else {
          Swal.fire(
            "Error!",
            `The selected Template you try to copy, can't be applied to any of your available Platforms.`,
            "error"
          );
        }
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Something goes wrong on checking Platform availability. Please try again later!`,
          "error"
        );
      }
    });
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleCloseModal = async (update, mode, name) => {
    if (update) {
      //console.log("Creation completed, please Update!");
      let setTemplatesEndpoint = null;
      let response = null;
      if (mode == "create") {
        setTemplatesEndpoint = `/api/v1/templates`;
        response = await postData(
          setTemplatesEndpoint,
          { platform_id: platform.value, name: name },
          {
            Authorization: getAuthorizationHeader(),
          }
        );
      }

      if (mode == "edit") {
        let templateEdited = dataTableData.rows.find((item) => item.id == recordToDelete);
        let selectedPlatformId = platform.value;
        let templateToUpdate = {
          name: name,
          platform_id: selectedPlatformId,
          status: templateEdited.status == "Enabled" ? true : false,
        };
        setTemplatesEndpoint = `/api/v1/templates/${templateEdited.id}`;
        response = await putData(setTemplatesEndpoint, templateToUpdate, {
          Authorization: getAuthorizationHeader(),
        });
      }

      setRecordToDelete(0);
      setRecordNameSelected(null);

      if (response) {
        Swal.fire(
          "Request Sent!",
          `Template ${mode == "create" ? "Created" : "Name Updated"} sucessfully!`,
          "success"
        );
        initData();
      } else {
        Swal.fire(
          "Error!",
          `Something goes wrong while ${
            mode == "create" ? "Creating" : "Updating Name of "
          } Template. Please try again later.`,
          "error"
        );
      }
    } else {
      //console.log("Creation canceled, do nothing!");
    }
    setCreateModal(false);
    if (update) {
      setNewName(name);
      // /fetchData();
    }
  };

  /**
   *
   * @param {*} update
   * @param {*} id
   */
  const handleDeleteCloseModal = async (update, id) => {
    if (update) {
      //console.log("Delete confirmed, please Update!");
      let setTemplatesEndpoint = `/api/v1/templates/${id}`;
      let response = await delData(setTemplatesEndpoint, {
        Authorization: getAuthorizationHeader(),
      });
      deleteRecord(id);

      if (response) initData();
    } else {
      //console.log("Delete canceled, do nothing!");
    }
    setDeleteModal(false);
  };

  /**
   *
   */
  async function initData() {
    if (!platform || platform == "") return;
    setLoading(true);

    await fetchOddsSettings();
    await fetchMarketsSettings();

    let defaultStaticOddReactionJson = { value: 0, operator: "+" };
    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/platforms/${selectedPlatformId}/templates`;
    let response = await getData(
      getTemplatesEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      response.data.map((el) => {
        el.selected = false;
        el.odds_filter_config_id = el.odds_setting_id;
        el.odds_filter_inplay_id = el.odds_setting_inplay;
        delete el.odds_setting_inplay;
        el.odds_filter_pregame_id = el.odds_setting_pregame;
        delete el.odds_setting_pregame;
        el.markets_filter_config_id = el.markets_setting_id;
        delete el.markets_setting_id;

        el.odds_filter_inplay_setting = oddSettingsList.find(
          (item) => item.id == el.odds_filter_inplay_id
        );
        el.odds_filter_pregame_setting = oddSettingsList.find(
          (item) => item.id == el.odds_filter_pregame_id
        );

        el.template = el.name;
        el.tpl_providers = el.providers_count ? `${el.providers_count} Providers, ` : "";
        el.tpl_sports = el.sports_count ? `${el.sports_count} Sports, ` : "";
        el.tpl_competitions = el.competitions_count ? `${el.competitions_count} Competitions` : "";
        el.filters = `${el.tpl_providers}${el.tpl_sports}${el.tpl_competitions}`;
        el.dis_markets = el.markets_disabled_count;

        if (el.odds_filter_inplay_setting) {
          el.oddsLimitReaction_inplay = oddsReactions.find(
            (or) => or.value == el.odds_filter_inplay_setting.barrier_reaction_type
          );
          el.oddsLimitReaction_inplay =
            el.oddsLimitReaction_inplay || el.oddsLimitReaction_inplay == 0
              ? el.oddsLimitReaction_inplay.label
              : "";
          el.lowerlimit_inplay = el.odds_filter_inplay_setting.min_odd_barrier;
          el.upperlimit_inplay = el.odds_filter_inplay_setting.max_odd_barrier;
          el.mixingType_inplay = mixingOptions.find(
            (mo) => mo.value == el.odds_filter_inplay_setting.mixing_odd_type
          );
          el.mixingType_inplay =
            el.mixingType_inplay || el.mixingType_inplay == 0 ? el.mixingType_inplay.label : "";
          el.mixing_providers_inplay =
            typeof el.odds_filter_inplay_setting.mixing_providers == "string"
              ? el.odds_filter_inplay_setting.mixing_providers.replace(/[{,},"]/g, "")
              : el.odds_filter_inplay_setting.mixing_providers;
          if (
            el.odds_filter_inplay_setting.mixing_providers == "NULL" ||
            !el.odds_filter_inplay_setting.mixing_providers
          ) {
            el.odds_filter_inplay_setting.mixing_providers = [];
          }
          el.mixingProviders_inplay =
            typeof el.odds_filter_inplay_setting.mixing_providers == "object"
              ? el.odds_filter_inplay_setting.mixing_providers.map((p) => p.name).join(",")
              : "";
          el.factor_inplay = el.odds_filter_inplay_setting.static_odd_reaction
            ? el.odds_filter_inplay_setting.static_odd_reaction.toString()
            : "0";
          el.randomFactor_inplay = el.odds_filter_inplay_setting.random_odd_reaction
            ? "Random Apply"
            : "Allways Apply";
          el.marginType_inplay = marginOptions.find(
            (mo) => mo.value == el.odds_filter_inplay_setting.margin_type
          );
          el.marginType_inplay =
            el.marginType_inplay || el.marginType_inplay == 0 ? el.marginType_inplay.label : "";
          el.marginPercent_inplay = el.odds_filter_inplay_setting.margin_percent;
          el.suspension_type_inplay = oddsSuspensionType.find(
            (ost) => ost.value == el.odds_filter_inplay_setting.suspension_type
          );
        }

        let listItems = [
          {
            title: "Market Suspension",
            settings: [
              {
                key: "Action",
                value:
                  el.suspension_type_inplay || el.suspension_type_inplay == 0
                    ? el.suspension_type_inplay.label
                    : "No Action",
              },
              {
                key: "Providers Priority for Suspension",
                value: el.suspension_provider_inplay
                  ? el.suspension_provider_inplay
                      .split(",")
                      .map((sp) => providers.find((p) => p.value == sp)["label"])
                  : "-",
              },
            ],
          },
          {
            title: "Odds Limit Reaction",
            settings: [
              { key: "Action", value: el.oddsLimitReaction_inplay },
              { key: "min Price", value: el.lowerlimit_inplay },
              { key: "max Price", value: el.upperlimit_inplay },
            ],
          },
          {
            title: "Mixing Type",
            settings: [
              { key: "Action", value: el.mixingType_inplay },
              {
                key: el.mixingType_inplay == "No Mixing" ? null : "Mixing",
                value: el.mixingProviders_inplay ? el.mixingProviders_inplay : "",
              },
              {
                key: "Mixing Odd Limit",
                value: el.mixing_odd_limit_inplay ? el.mixing_odd_limit_inplay : "-",
              },
            ],
          },
          {
            title: "Price Factor Behaviour",
            settings: [
              {
                key: "Action",
                value:
                  el.odds_filter_inplay_setting &&
                  el.odds_filter_inplay_setting.static_odd_reaction_json.length == 1 &&
                  JSON.stringify(el.odds_filter_inplay_setting.static_odd_reaction_json[0].value) ==
                    JSON.stringify(defaultStaticOddReactionJson.value)
                    ? "Do Nothing"
                    : "Prices will be manipulated",
              },
            ],
          },
          {
            title: "Margin Behaviour",
            settings: [
              {
                key: "Action",
                value: el.marginType_inplay,
              },
              {
                key: "Margin",
                value: el.marginPercent_inplay ? el.marginPercent_inplay + "%" : null,
              },
            ],
          },
        ];

        el.odd_settings_info_inplay = listItems;

        if (el.odds_filter_pregame_setting) {
          el.oddsLimitReaction_pregame = oddsReactions.find(
            (or) => or.value == el.odds_filter_pregame_setting.barrier_reaction_type
          );
          el.oddsLimitReaction_pregame =
            el.oddsLimitReaction_pregame || el.oddsLimitReaction_pregame == 0
              ? el.oddsLimitReaction_pregame.label
              : "";
          el.lowerlimit_pregame = el.odds_filter_pregame_setting.min_odd_barrier;
          el.upperlimit_pregame = el.odds_filter_pregame_setting.max_odd_barrier;
          el.mixingType_pregame = mixingOptions.find(
            (mo) => mo.value == el.odds_filter_pregame_setting.mixing_odd_type
          );
          el.mixingType_pregame =
            el.mixingType_pregame || el.mixingType_pregame == 0 ? el.mixingType_pregame.label : "";
          el.mixing_providers_pregame =
            typeof el.odds_filter_pregame_setting.mixing_providers == "string"
              ? el.odds_filter_pregame_setting.mixing_providers.replace(/[{,},"]/g, "")
              : el.odds_filter_pregame_setting.mixing_providers;
          if (
            el.odds_filter_pregame_setting.mixing_providers == "NULL" ||
            !el.odds_filter_pregame_setting.mixing_providers
          ) {
            el.odds_filter_pregame_setting.mixing_providers = [];
          }
          el.mixingProviders_pregame =
            typeof el.odds_filter_pregame_setting.mixing_providers == "object"
              ? el.odds_filter_pregame_setting.mixing_providers.map((p) => p.name).join(",")
              : "";
          el.factor_pregame = el.odds_filter_pregame_setting.static_odd_reaction
            ? el.odds_filter_pregame_setting.static_odd_reaction.toString()
            : "0";
          el.randomFactor_pregame = el.odds_filter_pregame_setting.random_odd_reaction
            ? "Random Apply"
            : "Allways Apply";
          el.marginType_pregame = marginOptions.find(
            (mo) => mo.value == el.odds_filter_pregame_setting.margin_type
          );
          el.marginType_pregame =
            el.marginType_pregame || el.marginType_pregame == 0 ? el.marginType_pregame.label : "";
          el.marginPercent_pregame = el.odds_filter_pregame_setting.margin_percent;
          el.suspension_type_pregame = oddsSuspensionType.find(
            (ost) => ost.value == el.odds_filter_pregame_setting.suspension_type
          );
        }

        listItems = [
          {
            title: "Market Suspension",
            settings: [
              {
                key: "Action",
                value:
                  el.suspension_type_pregame || el.suspension_type_pregame == 0
                    ? el.suspension_type_pregame.label
                    : "No Action",
              },
              {
                key: "Providers Priority for Suspension",
                value: el.suspension_provider_pregame
                  ? el.suspension_provider_pregame
                      .split(",")
                      .map((sp) => providers.find((p) => p.value == sp)["label"])
                  : "-",
              },
            ],
          },
          {
            title: "Odds Limit Reaction",
            settings: [
              { key: "Action", value: el.oddsLimitReaction_pregame },
              { key: "min Price", value: el.lowerlimit_pregame },
              { key: "max Price", value: el.upperlimit_pregame },
            ],
          },
          {
            title: "Mixing Type",
            settings: [
              { key: "Action", value: el.mixingType_pregame },
              {
                key: el.mixingType_pregame == "No Mixing" ? null : "Mixing",
                value: el.mixingProviders_pregame ? el.mixingProviders_pregame : "",
              },
              {
                key: "Mixing Odd Limit",
                value: el.mixing_odd_limit_pregame ? el.mixing_odd_limit_pregame : "-",
              },
            ],
          },
          {
            title: "Price Factor Behaviour",
            settings: [
              {
                key: "Action",
                value:
                  el.odds_filter_pregame_setting &&
                  el.odds_filter_pregame_setting.static_odd_reaction_json.length == 1 &&
                  JSON.stringify(
                    el.odds_filter_pregame_setting.static_odd_reaction_json[0].value
                  ) == JSON.stringify(defaultStaticOddReactionJson.value)
                    ? "Do Nothing"
                    : "Prices will be manipulated",
              },
            ],
          },
          {
            title: "Margin Behaviour",
            settings: [
              {
                key: "Action",
                value: el.marginType_pregame,
              },
              {
                key: "Margin",
                value: el.marginPercent_pregame ? el.marginPercent_pregame + "%" : null,
              },
            ],
          },
        ];

        el.odd_settings_info_pregame = listItems;

        el.action = (
          <ActionCell
            id={el.id}
            status={el.status}
            filters={el.filters}
            handleEditAction={(e) => handleEditRecord(e, el.id, el.template)}
            handleDeleteAction={(e) => handleOpenModal(e, el.id)}
            handleApplyAction={(e) => handleApplyAction(e, el)}
            handleCheckCloneAction={(e) => handleCheckCloneAction(e, el)}
          />
        );
        el.status = el.status ? "Enabled" : "Disabled";
      });
    } else {
    }

    let data = { ...dataTableData };
    data.rows = response && response.data ? response.data : data.rows;

    currentData = { ...data };

    //data.rows.sort((a, b) => b.id - a.id);

    setHiddenColumns(
      oddSettingsShowCached
        ? ["selected"]
        : ["selected", "odds_filter_inplay_id", "odds_filter_pregame_id"]
    );

    //console.log("Init Data");
    setDataTableData(data);
    setLoading(false);
  }

  /**
   *
   * @returns
   */
  function fetchData() {
    //console.log("updateRecords: ", updateRecords);
    if (!dataTableData.rows) return;
    let initData = { ...dataTableData };
    let initDataRows = [...dataTableData.rows];
    let addedTemplate = {
      id: initDataRows.length + 1,
      template: newName,
      filters: "",
      status: "Disabled",
      action: (
        <ActionCell
          id={initDataRows.length + 1}
          status={initDataRows.status}
          handleDeleteAction={(e) => handleOpenModal(e, initDataRows.length + 1)}
        />
      ),
    };

    if (updateRecords) {
      //console.log("initData.rows 1: ", initData.rows.length);
      initDataRows.push(addedTemplate);
      initDataRows.sort((a, b) => b.id - a.id);
      initData.rows = initDataRows;
      //console.log("initData.rows 2: ", initData.rows.length);
      setDataTableData(initData);
      setTemplateCreatedId(initDataRows.length + 1);
      setNewName("");
      showAlert();
    }
    setUpdateRecords(false);
  }

  /**
   *
   * @param {*} id
   */
  function deleteRecord(id) {
    //console.log("deleteRecord:", id);
    let initData = { ...dataTableData };
    let initDataRows = [...dataTableData.rows];

    let indexToRemove = initDataRows.findIndex((template) => template.id === id);
    initDataRows.splice(indexToRemove, 1);

    initData.rows = initDataRows;
    setDataTableData(initData);
  }

  /**
   *
   * @returns
   */
  const showAlert = () =>
    Swal.fire({
      title: "You are about to redirect automatically to Template Edit Page!",
      html: "in <b></b> milliseconds.",
      timer: 5000,
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
        timerInterval = setInterval(() => {
          const content = Swal.getHtmlContainer();
          if (content) {
            const b = content.querySelector("b");
            if (b) {
              b.textContent = Swal.getTimerLeft();
            }
          }
        }, 100);
      },
      willClose: () => {
        clearInterval(timerInterval);
      },
    }).then((result) => {
      /* Read more about handling dismissals below */
      if (result.dismiss === Swal.DismissReason.timer) {
        //console.log("I was closed by the timer");
        initData();
      }
    });

  async function fetchOddsSettings() {
    // if (oddSettingsOptions.length > 0) return;
    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/oddsfilters/platforms/${selectedPlatformId}`;
    let response = await getData(
      getTemplatesEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      oddSettingsList = response.data;
      oddSettingsOptions = response.data.map((r) => {
        return { value: r.id, label: r.name };
      });
      setSettingsAvailable(oddSettingsOptions);
    }
  }

  async function fetchMarketsSettings() {
    // if (oddSettingsOptions.length > 0) return;
    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/marketsfilters/platform/${selectedPlatformId}`;
    let response = await getData(
      getTemplatesEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {      marketSettingsOptions = response.data.map((r) => {
        return { value: r.id, label: r.name };
      });
      setMarketSettingsAvailable(marketSettingsOptions);
    }
  }

  async function handleSignals(e, id, row, filtered) {
    e.preventDefault();
    //console.log(row);
    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });

    let dataRow = currentData.rows.find((item) => item.id == row.original.id);
    if (e.type != "OddSettingsChange" && e.type != "MarketSettingsChange") {
      if (dataRow.hasOwnProperty("operFiltered")) {
        dataRow.operFiltered = !dataRow.operFiltered;
        if (!filtered && !dataRow.operFiltered) {
          delete dataRow.operFiltered;
        }
        if (filtered && dataRow.operFiltered) {
          delete dataRow.operFiltered;
        }
      } else {
        dataRow.operFiltered = filtered ? false : true;
        if (dataRow.operFiltered) dataRow.modified = true;
        else dataRow.modified = false;
      }
    }

    const marketFilter = {
      filter_id: id,
      provider_id: row.original.provider_id,
      platform_id: userData.defaultPlatform.value,
      template_id: id,
      filtered: dataRow.filtered,
      operFiltered: dataRow.operFiltered,
      odds_setting_inplay: dataRow.odds_setting_inplay
        ? dataRow.odds_setting_inplay
        : dataRow.odds_filter_inplay_id,
      odds_setting_pregame: dataRow.odds_setting_pregame
        ? dataRow.odds_setting_pregame
        : dataRow.odds_filter_pregame_id,
      sport_id: dataRow.sport_id,
      odds_filter_config_id: dataRow.odds_filter_config_id,
      markets_filter_config_id: dataRow.markets_setting_id
        ? dataRow.markets_setting_id
        : dataRow.markets_filter_config_id,
      type: dataRow.type,
    };

    const marketFilterIndex = templateOddsFilters.findIndex(
      (item) => item.filter_id === marketFilter.filter_id
    );

    if (
      !dataRow.hasOwnProperty("operFiltered") &&
      !dataRow.hasOwnProperty("odds_setting_inplay") &&
      !dataRow.hasOwnProperty("odds_setting_pregame") &&
      !dataRow.hasOwnProperty("markets_setting_id")
    ) {
      if (marketFilterIndex > -1) {
        templateOddsFilters.splice(marketFilterIndex, 1);
        dataRow.modified = false;
      }
      if (templateOddsFilters.length > 0) setUpdateEnabled(true);

      if (marketFilterIndex > -1 && templateOddsFilters.length == 0) {
        setApiresponse("error");
        setApiResponseMsg("There are NO unsaved filters changes.");
        setNotification(true);
        setShowDialog(false);
      }

      setHiddenColumns(
        oddSettingsShow
          ? ["selected"]
          : ["selected", "odds_filter_inplay_id", "odds_filter_pregame_id"]
      );
      setDataTableData(currentData);

      return;
    } else {
      dataRow.modified = true;
    }

    if (
      dataRow.hasOwnProperty("odds_setting_inplay") ||
      dataRow.hasOwnProperty("odds_setting_pregame")
    ) {
      marketFilter.odds_setting_change = true;
    }
    if (dataRow.hasOwnProperty("markets_setting_id")) {
      marketFilter.market_setting_change = true;
    }

    setHiddenColumns(
      oddSettingsShowCached
        ? ["selected"]
        : ["selected", "odds_filter_inplay_id", "odds_filter_pregame_id"]
    );
    setDataTableData(currentData);

    if (marketFilterIndex < 0) {
      templateOddsFilters.push(marketFilter);
    } else {
      templateOddsFilters[marketFilterIndex] = marketFilter;
    }

    if (templateOddsFilters.length > 0) {
      setUpdateEnabled(true);
      setApiresponse("success");
      setApiResponseMsg("There are unsaved filters changes.");
      setNotification(true);
      setShowDialog(true);
    }
  }

  const handleTableDataChanges = async ({ key, value, extra }) => {
    //console.log(key, ":", value, extra);
    if (key == "refresh") {
      initData();
    }

    if (key == "selectOne") {
      setUpdateEnabled(false);
      setHiddenColumns([]);
      setDataTableData({ columns: [], rows: [] });

      let dataRow = currentData.rows.find((item) => item.id == value);
      dataRow.selected = extra;
      let rows = selectedRows;
      let rowExist = rows.findIndex((item) => item.id == value);
      if (rowExist > -1) {
        if (!extra) {
          rows.splice(rowExist, 1);
          setSelectedRows(rows);
        }
      } else {
        if (extra) {
          rows.push(dataRow);
          setSelectedRows(rows);
        }
      }
      if (templateOddsFilters.length > 0) setUpdateEnabled(true);

      setHiddenColumns(
        oddSettingsShow
          ? ["selected"]
          : ["selected", "odds_filter_inplay_id", "odds_filter_pregame_id"]
      );
      setDataTableData(currentData);
    }

    if (key == "selectAll") {
      setUpdateEnabled(false);
      setHiddenColumns([]);
      setDataTableData({ columns: [], rows: [] });
      let dataRows = currentData.rows;
      if (extra == "all") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = true;
        }
        setSelectedRows(dataRows);
      } else if (extra == "none") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = false;
        }
        setSelectedRows([]);
      }
      if (templateOddsFilters.length > 0) setUpdateEnabled(true);

      setHiddenColumns(
        oddSettingsShow
          ? ["selected"]
          : ["selected", "odds_filter_inplay_id", "odds_filter_pregame_id"]
      );
      setDataTableData(currentData);
    }
  };

  function marketSettingsChange(item, type) {
    for (let index = 0; index < selectedRows.length; index++) {
      const filter = selectedRows[index];
      filter[`markets_setting_id`] = item.value;
      filter.modified = true;
      handleSignals(
        new Event("MarketSettingsChange"),
        filter.id,
        { original: filter },
        filter.filtered
      );
    }
  }

  function oddSettingsChange(item, type) {
    for (let index = 0; index < selectedRows.length; index++) {
      const filter = selectedRows[index];
      filter[`odds_setting_${type}`] = item.value;
      filter.modified = true;
      handleSignals(
        new Event("OddSettingsChange"),
        filter.id,
        { original: filter },
        filter.filtered
      );
    }
  }

  function handleOddSettingSelection(e, { action, data, type }) {
    e.preventDefault();

    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });
    let row = currentData.rows.find((row) => row.id == data);
    let sendSignal = false;

    if (action == "edit") {
      if (row) {
        if (type == "inplay") {
          row.operOddSettings_inplay = row.operOddSettings_inplay ? false : true;
          if (row.operOddSettings_inplay) {
            row.odds_setting_inplay = row.odds_filter_inplay_id;
          } else {
            if (row.odds_filter_inplay_id) {
              delete row.odds_setting_inplay;
              sendSignal = true;
            }
          }
        }
        if (type == "pregame") {
          row.operOddSettings_pregame = row.operOddSettings_pregame ? false : true;
          if (row.operOddSettings_pregame) {
            row.odds_setting_pregame = row.odds_filter_pregame_id;
          } else {
            if (row.odds_filter_pregame_id) {
              delete row.odds_setting_pregame;
              sendSignal = true;
            }
          }
        }
      }
    }

    if (action == "add") {
      if (row) {
        if (
          row[`odds_setting_${type}`] == row[`odds_filter_${type}_id`] &&
          row[`odds_setting_${type}`] != null
        ) {
          delete row[`odds_setting_${type}`];
          sendSignal = true;
        }
        row[`operOddSettings_${type}`] = row[`operOddSettings_${type}`] ? false : true;
      }
    }

    if (action == "remove") {
      if (row) {
        delete row[`odds_setting_${type}`];
        delete row[`operOddSettings_${type}`];
        sendSignal = true;
      }
    }

    if (action == "delete") {
      if (row) {
        row[`odds_filter_${type}_id`] = null;
        row[`odds_setting_${type}`] = null;
        row[`operOddSettings_${type}`] = false;
        sendSignal = true;
      }
    }

    if (sendSignal)
      handleSignals(new Event("OddSettingsChange"), row.id, { original: row }, row.filtered);

    if (templateOddsFilters.length > 0) setUpdateEnabled(true);
    setHiddenColumns(
      oddSettingsShowCached
        ? ["selected"]
        : ["selected", "odds_filter_inplay_id", "odds_filter_pregame_id"]
    );
    setDataTableData(currentData);
  }

  function rowOddSettingsChange(item, row, type) {
    row[`odds_setting_${type}`] = item.value;
    row.modified = true;
    row[`operOddSettings_${type}`] = false;
    handleSignals(new Event("OddSettingsChange"), row.id, { original: row }, row.filtered);
  }

  function handleMarketSettingSelection(e, { action, data }) {
    e.preventDefault();

    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });
    let row = currentData.rows.find((row) => row.id == data);
    let sendSignal = false;

    if (action == "edit") {
      if (row) {
        row.operMarketSettings_id = row.operMarketSettings_id ? false : true;
        if (row.operMarketSettings_id) {
          row.markets_setting_id = row.markets_filter_config_id;
        } else {
          if (row.markets_filter_config_id) {
            delete row.markets_setting_id;
            sendSignal = true;
          }
        }
      }
    }

    if (action == "add") {
      if (row) {
        if (
          row[`markets_setting_id`] == row[`markets_filter_config_id`] &&
          row[`markets_setting_id`] != null
        ) {
          delete row[`markets_setting_id`];
          sendSignal = true;
        }
        row[`operMarketSettings_id`] = row[`operMarketSettings_id`] ? false : true;
      }
    }

    if (action == "remove") {
      if (row) {
        delete row[`markets_setting_id`];
        delete row[`operMarketSettings_id`];
        sendSignal = true;
      }
    }

    if (action == "delete") {
      if (row) {
        row[`markets_filter_config_id`] = null;
        row[`markets_setting_id`] = null;
        row[`operMarketSettings_id`] = false;
        sendSignal = true;
      }
    }

    if (sendSignal)
      handleSignals(new Event("MarketSettingsChange"), row.id, { original: row }, row.filtered);

    if (templateOddsFilters.length > 0) setUpdateEnabled(true);
    setHiddenColumns(
      oddSettingsShowCached
        ? ["selected"]
        : ["selected", "odds_filter_inplay_id", "odds_filter_pregame_id"]
    );
    setDataTableData(currentData);
  }

  function rowMarketSettingsChange(item, row) {
    row[`markets_setting_id`] = item.value;
    row.modified = true;
    row[`operMarketSettings_id`] = false;
    handleSignals(new Event("MarketSettingsChange"), row.id, { original: row }, row.filtered);
  }

  const getTemplatesOddsFilters = () => {
    return templateOddsFilters;
  };

  const handleUpdate = () => {
    let selectedPlatformId = platform.value;
    Swal.fire({
      title: "We proccess your request! ",
      html: "Wait for few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        const oddsSettingsUpdateEndpoint = `/api/v1/oddsfilters/platform/${selectedPlatformId}/configs/assign`;
        const marketsSettingsUpdateEndpoint = `/api/v1/marketsfilters/platform/${selectedPlatformId}/config/assign`;
        const templatesOddsFiltersVar = getTemplatesOddsFilters();
        let oddsSettings = [];
        let marketsSettings = [];

        for (let index = 0; index < templatesOddsFiltersVar.length; index++) {
          const template = templatesOddsFiltersVar[index];
          if (template.market_setting_change) {
            let marketsSetting = {
              template_id: template.template_id,
              provider_id: null,
              sport_id: null,
              competition_id: null,
              market_filter_config_id: template.markets_filter_config_id,
            };
            marketsSettings.push(marketsSetting);
          }
          if (template.odds_setting_change) {
            delete template.markets_filter_config_id;
            delete template.odds_setting_change;
            delete template.market_setting_change;
            oddsSettings.push(template);
          }
        }

        let response = null;
        if (oddsSettings.length > 0) {
          response = await putData(oddsSettingsUpdateEndpoint, oddsSettings, {
            Authorization: getAuthorizationHeader(),
          });
        }

        if (marketsSettings.length > 0) {
          response = await putData(marketsSettingsUpdateEndpoint, marketsSettings, {
            Authorization: getAuthorizationHeader(),
          });
        }

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      if (result.dismiss === Swal.DismissReason.timer) {
        //console.log("I was closed by the timer");
        //routeChange();
      }

      setUpdateEnabled(true);
      setUpdateEnabled(false);
      resetData();
      setShowDialog(false);
      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  };

  const handleCancelEdit = async () => {
    if (templateOddsFilters.length > 0) {
      Swal.fire({
        title: `Unsaved Data!`,
        html:
          "You are going Lose some Odds Settings you have already set.</br></br>" +
          templateOddsFilters.length +
          " Odds Settings have been changed. </br></br> Are you Sure?",
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes, Cancel them!",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          //console.log("Modal confirmed");
          setUpdateEnabled(true);
          setUpdateEnabled(false);
          resetData();
          setShowDialog(false);
        }
      });
    } else {
      //console.log("Nothing have been changed");
    }
  };

  async function resetData() {
    templateOddsFilters = [];
    setSelectedRows([]);
    setShowDialog(false);
    initData();
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  const handlePermissions = (permissions) => {
    setUserPermissions(permissions);
    let editOddSettingsEnabled = false;
    let showOddSettingsEnabled = false;

    //console.log(permissions);

    for (let index = 0; index < permissions.length; index++) {
      const permission = permissions[index];
      if (permission == "super") {
        editOddSettingsEnabled = true;
        showOddSettingsEnabled = true;
      }
      if (permission == "feed-settings-odds-edit") {
        editOddSettingsEnabled = true;
        showOddSettingsEnabled = true;
        break;
      }
      if (permission == "feed-settings-odds-show") {
        showOddSettingsEnabled = true;
      }
    }

    setOddSettingsEdit(editOddSettingsEnabled);
    oddSettingsEditCached = editOddSettingsEnabled;
    setOddSettingsShow(showOddSettingsEnabled);
    oddSettingsShowCached = showOddSettingsEnabled;
  };

  useEffect(() => {
    if (!platform && permissions) return;
    if (permissions.length > 0) handlePermissions(permissions);
    if (platform && permissions.length > 0) initData();
  }, [platform.label, permissions]);

  useEffect(() => {
    return () => {
      if (abortController) {
        // console.log("abortController");
        abortController.abort();
      }
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <CreateTemplate
        id={recordToDelete}
        name={recordNameSelected}
        showModal={showCreateModal}
        signalCloseModal={handleCloseModal}
      ></CreateTemplate>
      <CheckObjectDeletion
        referenceId={recordToDelete}
        reference={"template"}
        showModal={showDeleteModal}
        signalCloseModal={handleDeleteCloseModal}
      ></CheckObjectDeletion>
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Your Templates
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can find all the saved Templates you have for the current Platform
              </SoftTypography>
            </SoftBox>
            <Stack spacing={1} direction="row">
              <SoftButton variant="gradient" color="info" size="small" onClick={handleCreateRecord}>
                + new Template
              </SoftButton>
            </Stack>
          </SoftBox>
          <SoftBox p={3} sx={{ paddingTop: "0px !important", zIndex: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={9} md={9}></Grid>
              <Grid item xs={3} md={3} style={{ textAlign: "right" }}>
                <SoftBox lineHeight={1} sx={{ display: oddSettingsEditCached ? "" : "none" }}>
                  <Button onClick={handleCancelEdit} disabled={updateEnabled ? false : true}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdate} disabled={updateEnabled ? false : true}>
                    Update
                  </Button>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
          <SoftBox
            pt={2}
            pl={3}
            pr={3}
            sx={{
              display: oddSettingsEditCached ? (selectedRows.length > 0 ? "" : "none") : "none",
            }}
            className="table-multiselected-action-template"
          >
            <Grid
              container
              sx={{
                backgroundImage: "linear-gradient(to right, #FFFFFF, #f8f9fa, #ffffff)",
                minHeight: "90px",
                maxHeight: "90px",
                color: "red !important",
                // textAlign: "right !important",
              }}
            >
              <Grid
                item
                mr={2}
                md={6}
                sm={6}
                sx={{
                  textAlign: "right !important",
                  width: "200px !important",
                  maxWidth: "fit-content !important",
                  // paddingLeft: "50px !important",
                }}
              >
                <SoftTypography variant="caption"> </SoftTypography>
                <SoftTypography
                  variant="caption"
                  style={{ textAlign: "right !important", lineHeight: 10 }}
                >
                  Apply to the selected Templates:{" "}
                </SoftTypography>
              </Grid>
              <Grid item mr={2} style={{ zIndex: 3 }}>
                <SoftTypography variant="caption">Markets Settings: </SoftTypography>
                <SoftSelect
                  size="small"
                  customSmall={true}
                  options={getMarketSettingsOptions()}
                  onChange={(item) => marketSettingsChange(item, "inplay")}
                ></SoftSelect>
              </Grid>
              <Grid item mr={2} style={{ zIndex: 3 }}>
                <SoftTypography variant="caption">Odds Settings inPlay: </SoftTypography>
                <SoftSelect
                  size="small"
                  customSmall={true}
                  options={getOddSettingsOptions()}
                  onChange={(item) => oddSettingsChange(item, "inplay")}
                ></SoftSelect>
              </Grid>
              <Grid item mr={2} style={{ zIndex: 3 }}>
                <SoftTypography variant="caption">Odds Settings preGame: </SoftTypography>
                <SoftSelect
                  size="small"
                  customSmall={true}
                  options={getOddSettingsOptions()}
                  onChange={(item) => oddSettingsChange(item, "pregame")}
                ></SoftSelect>
              </Grid>
            </Grid>
          </SoftBox>
          <DataTable
            table={dataTableData}
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            openBackDrop={loading}
            hiddenColumns={hiddenColumns}
            autoResetPageDefault={true}
            autoResetGlobalFilter={true}
            autoResetSortBy={true}
            canSearch
            checkboxSelection={oddSettingsEditCached}
            refreshEnabled={true}
            informParent={handleTableDataChanges}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default TemplatesList;
